import { useCallback, useEffect, useMemo, useState } from "react";

import { SortingState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";

import useIntelligentPricingReview from "../../data/useIntelligentPricingReview";
import useListingOptimisation from "../../data/useListingOptimisation";
import useListingReview from "../../data/useListingReview";
import usePartAiTitleBulk from "../../data/usePartAiTitleBulk";
import usePartBulkScrapOut from "../../data/usePartBulkScrapOut";
import usePartBulkStatus from "../../data/usePartBulkStatus";
import usePartsLookupMutations from "../../data/usePartsLookupMutations";
import useTemplateProductDescription from "../../data/useTemplateProductDescription";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useUserLookup } from "../../hooks/useUserLookup";
import Modal from "../../layouts/Modal";
import { IPart, PartStatus } from "../../models/Part";
import { PageMode } from "../../models/PagedTable";
import { IListingOptimisation, IStageListingOptimisationItem, ListingOptimisationStatus } from "../../models/listingOptimisation/IListingOptimisation";
import { IntelligencePricingOutcome, normaliseIntelligencePricingOutcome } from "../../models/listingOptimisation/IListingReview";
import { summarisePrices } from "../../utils/intelligencePricing";
import PriceFormat from "../../utils/priceFormat";
import BulkEditListingSpecifics from "../ebayListings/BulkEditListingSpecifics";
import BulkRelistResultModal, { BulkRelistResultData } from "../ebayListings/BulkRelistResultModal";
import BulkUpdatePoliciesModal, { IBulkUpdatePolicies } from "../ebayListings/listedTable/BulkUpdatePoliciesModal";
import { computeEbayListingTitle } from "../ebayListings/EbayListingTitle";
import useNotification from "../notifications/useNotifications";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import PagedTable from "../shared/Tables/PagedTable";
import BulkBar, { IBulkRunDetail, IBulkRunSummary } from "./BulkBar";
import FocusDrawer from "./FocusDrawer";
import PhotoManagerModal, { StagedImageFlag } from "./PhotoManagerModal";
import QueueFilters from "./QueueFilters";
import { buildQueueColumns } from "./QueueRow";
import RelistConfirmModal from "./RelistConfirmModal";
import { IQueueFilters, QUEUE_SORT_OPTIONS, buildQueueSearchBody, defaultQueueFilters, sortingForQueueSort } from "./queueFilterState";

interface IEbayOptimisationQueueProps {
  filters: IQueueFilters;
  onFiltersChange: (next: Partial<IQueueFilters>) => void;
  onClearFilters: () => void;
  /** The rows on the page, for the header's rule badge. */
  onRowsChange: (rows: IPart[]) => void;
}

type ConfirmKind = "relist" | "qc" | "scrapOut" | "discard" | null;

/**
 * The triage queue itself: filters, the listings table, the bulk bar over the selection, and the
 * focus drawer and photo manager for one listing at a time. Selection is a set of part ids kept
 * here rather than in the table, so every action can read it.
 */
export default function EbayOptimisationQueue({ filters, onFiltersChange, onClearFilters, onRowsChange }: IEbayOptimisationQueueProps) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { findUser } = useUserLookup();
  const { saveHandlers } = useDefaultCRUDHandlers("Staged changes");
  const { pendingOptimisations, stageChanges, discardChanges, sendToRelist } = useListingOptimisation("EbayOptimisationQueue");
  const { markReviewed } = useListingReview();
  const { priceFromIntelligence } = useIntelligentPricingReview(undefined, "EbayOptimisationQueue");
  const { generateAiTitles } = usePartAiTitleBulk();
  const { bulkSetStatus } = usePartBulkStatus();
  const { bulkScrapOut } = usePartBulkScrapOut();
  const { intelligencePricingBulk } = usePartsLookupMutations("");
  const { bulkUpdateProductDescription } = useTemplateProductDescription();

  const [pageRows, setPageRows] = useState<IPart[]>([]);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [photosPart, setPhotosPart] = useState<IPart | null>(null);
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; parts: IPart[] }>({ kind: null, parts: [] });
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [specificsOpen, setSpecificsOpen] = useState(false);
  const [relistResult, setRelistResult] = useState<BulkRelistResultData | null>(null);
  const [relistResultOpen, setRelistResultOpen] = useState(false);
  const [lastRun, setLastRun] = useState<IBulkRunSummary | null>(null);
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());
  const [lookupRunning, setLookupRunning] = useState(false);

  const additionalBody = useMemo(() => buildQueueSearchBody(filters), [filters]);
  const sorting = useMemo(() => sortingForQueueSort(filters.sort), [filters.sort]);

  const handleSortingChange = useCallback(
    (next: SortingState) => {
      const option = QUEUE_SORT_OPTIONS.find((candidate) => candidate.sorting[0].id === next[0]?.id);
      if (option && option.key !== filters.sort) onFiltersChange({ sort: option.key });
    },
    [filters.sort, onFiltersChange]
  );

  const optimisationsByPartId = useMemo(() => {
    const map = new Map<string, IListingOptimisation>();
    pendingOptimisations.data?.data
      ?.filter((optimisation) => optimisation.status === ListingOptimisationStatus.Pending)
      .forEach((optimisation) => map.set(optimisation.partDataId, optimisation));
    return map;
  }, [pendingOptimisations.data]);

  const handleRowData = useCallback(
    (rows: IPart[]) => {
      setPageRows(rows);
      onRowsChange(rows);
      // Rows that left the page (relisted, scrapped, filtered) leave the selection too.
      setSelectedIds((current) => {
        const ids = new Set(rows.map((row) => row.id));
        const next = new Set(Array.from(current).filter((id) => ids.has(id)));
        return next.size === current.size ? current : next;
      });
    },
    [onRowsChange]
  );

  const rowsById = useMemo(() => new Map(pageRows.map((row) => [row.id, row])), [pageRows]);
  const selectedRows = useMemo(() => pageRows.filter((row) => selectedIds.has(row.id)), [pageRows, selectedIds]);
  const focusedPart = focusedId ? rowsById.get(focusedId) ?? null : null;

  // The drawer walks the selection when the focused listing is in it, else the page.
  const walkList = useMemo(
    () => (focusedId && selectedIds.has(focusedId) && selectedRows.length > 0 ? selectedRows : pageRows),
    [focusedId, selectedIds, selectedRows, pageRows]
  );
  const walkIndex = focusedId ? walkList.findIndex((row) => row.id === focusedId) : -1;

  useEffect(() => {
    if (focusedId && !rowsById.has(focusedId)) setFocusedId(null);
  }, [focusedId, rowsById]);

  const focusOffset = useCallback(
    (offset: number) => {
      if (walkIndex < 0 || walkList.length === 0) return;
      const next = walkList[(walkIndex + offset + walkList.length) % walkList.length];
      setFocusedId(next.id);
    },
    [walkIndex, walkList]
  );

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((current) => {
      const allSelected = pageRows.length > 0 && pageRows.every((row) => current.has(row.id));
      return allSelected ? new Set() : new Set(pageRows.map((row) => row.id));
    });
  }, [pageRows]);

  const findUserName = useCallback((userId: string | null | undefined) => (userId ? findUser(userId)?.fullname ?? null : null), [findUser]);

  // Staging replaces the whole record, so every action carries over what is already staged.
  const buildCarryOverItem = useCallback(
    (partId: string): IStageListingOptimisationItem => {
      const existing = optimisationsByPartId.get(partId);
      return {
        partDataId: partId,
        newTitle: existing?.newTitle ?? null,
        newPrice: existing?.newPrice ?? null,
        newFulfillmentPolicyId: existing?.newFulfillmentPolicyId ?? null,
        newPaymentPolicyId: existing?.newPaymentPolicyId ?? null,
        newReturnPolicyId: existing?.newReturnPolicyId ?? null,
        newLocationKey: existing?.newLocationKey ?? null,
        applyImageRules: existing?.applyImageRules ?? false,
        removeBackgroundFirstImage: existing?.removeBackgroundFirstImage ?? false,
        applyCategoryDefaults: existing?.applyCategoryDefaults ?? false,
      };
    },
    [optimisationsByPartId]
  );

  const stage = useCallback(
    (items: IStageListingOptimisationItem[], onSuccess?: () => void) => {
      stageChanges.mutate(items, {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          onSuccess?.();
        },
      });
    },
    [stageChanges, saveHandlers]
  );

  const recordRun = (action: string, summary: string, details: IBulkRunDetail[]) =>
    setLastRun({ action, at: new Date().toISOString(), summary, details });

  const labelFor = (part: IPart | undefined, partId: string, tag?: string | null) => tag || part?.tagNumber || partId.slice(0, 8);

  const requireSelection = (): IPart[] | null => {
    if (selectedRows.length === 0) {
      addNotification({ primaryText: "No listings selected", variant: "warn" });
      return null;
    }
    return selectedRows;
  };

  const handleIntelligentPricing = () => {
    const rows = requireSelection();
    if (!rows) return;
    priceFromIntelligence.mutate(
      rows.map((row) => row.id),
      {
        onSuccess: (response) => {
          const results = response.data ?? [];
          let priced = 0;
          let held = 0;
          let noData = 0;
          let unchanged = 0;
          const details: IBulkRunDetail[] = results.map((result) => {
            const outcome = normaliseIntelligencePricingOutcome(result.outcome);
            const label = labelFor(rowsById.get(result.partId), result.partId, result.tagNumber);
            switch (outcome) {
              case IntelligencePricingOutcome.Priced:
                priced += 1;
                return { partId: result.partId, label, tone: "ok", text: `Staged ${PriceFormat(result.stagedPrice ?? result.average ?? 0)} from ${result.keptCount} of ${result.foundCount}` };
              case IntelligencePricingOutcome.Held:
                held += 1;
                return { partId: result.partId, label, tone: "held", text: `Held: ${result.reason ?? "not enough matches"}` };
              case IntelligencePricingOutcome.Unchanged:
                unchanged += 1;
                return { partId: result.partId, label, tone: "skipped", text: "Already at the intelligent price" };
              default:
                noData += 1;
                return { partId: result.partId, label, tone: "skipped", text: result.reason ?? "No priced listings found" };
            }
          });
          const summary = [`${priced} priced`, held > 0 ? `${held} held for review` : null, noData > 0 ? `${noData} no data` : null, unchanged > 0 ? `${unchanged} unchanged` : null]
            .filter(Boolean)
            .join(", ");
          recordRun("Intelligent pricing + AI review", summary, details);
          addNotification({ primaryText: `Intelligent pricing: ${summary}`, variant: priced > 0 ? "success" : "warn" });
        },
        onError: () => addNotification({ primaryText: "Intelligent pricing failed", variant: "error" }),
      }
    );
  };

  const handleAiTitles = () => {
    const rows = requireSelection();
    if (!rows) return;
    generateAiTitles.mutate(
      rows.map((row) => row.id),
      {
        onSuccess: (response) => {
          const results = response.data ?? [];
          const staged = results.filter((result) => result.staged).length;
          const details: IBulkRunDetail[] = results.map((result) => ({
            partId: result.partId,
            label: labelFor(rowsById.get(result.partId), result.partId, result.tagNumber),
            tone: result.staged ? "ok" : "held",
            text: result.staged
              ? `Staged · ${result.characterCount}/80 · score ${result.score}`
              : `Not staged: ${result.reason ?? result.warnings.join("; ") ?? "no title"}`,
          }));
          const summary = `${staged} title${staged === 1 ? "" : "s"} staged${results.length - staged > 0 ? `, ${results.length - staged} not staged` : ""}`;
          recordRun("AI title builder", summary, details);
          addNotification({ primaryText: `AI titles: ${summary}`, variant: staged > 0 ? "success" : "warn" });
        },
        onError: () => addNotification({ primaryText: "AI title builder failed", variant: "error" }),
      }
    );
  };

  const handleStageWilmaTitles = () => {
    const rows = requireSelection();
    if (!rows) return;
    const items: IStageListingOptimisationItem[] = [];
    rows.forEach((row) => {
      const wilmaTitle = (computeEbayListingTitle(row, null, true) ?? "").trim();
      if (!wilmaTitle) return;
      const base = buildCarryOverItem(row.id);
      items.push({ ...base, newTitle: wilmaTitle !== row.offers?.[0]?.title ? wilmaTitle : base.newTitle });
    });
    if (items.length === 0) {
      addNotification({ primaryText: "No Wilma titles could be built for the selected listings", variant: "warn" });
      return;
    }
    stage(items, () => recordRun("Stage Wilma titles", `${items.length} title${items.length === 1 ? "" : "s"} staged`, []));
  };

  const handleMarkReviewed = (parts: IPart[], thenNext = false) => {
    if (parts.length === 0) return;
    const ids = parts.map((part) => part.id);
    setMarkingIds(new Set(ids));
    markReviewed.mutate(ids, {
      onSuccess: () => {
        addNotification({ primaryText: `${ids.length} listing${ids.length === 1 ? "" : "s"} marked reviewed`, variant: "success" });
        if (thenNext) focusOffset(1);
      },
      onError: () => addNotification({ primaryText: "Failed to mark reviewed", variant: "error" }),
      onSettled: () => setMarkingIds(new Set()),
    });
  };

  const openConfirm = (kind: ConfirmKind, parts: IPart[]) => {
    if (parts.length === 0) {
      addNotification({ primaryText: "No listings selected", variant: "warn" });
      return;
    }
    setConfirm({ kind, parts });
  };

  const closeConfirm = () => setConfirm({ kind: null, parts: [] });

  const handleRelist = (appendSku: boolean) => {
    const ids = confirm.parts.map((part) => part.id);
    closeConfirm();
    sendToRelist.mutate(
      { partDataIds: ids, appendSku },
      {
        onSuccess: (response) => {
          setRelistResult(response.data);
          setRelistResultOpen(true);
          recordRun("Relist", `${response.data?.successful?.length ?? 0} relisted, ${response.data?.unsuccessful?.length ?? 0} failed`, []);
        },
        onError: () => addNotification({ primaryText: "Failed to send listings to be relisted", variant: "error" }),
      }
    );
  };

  const handleSendToQc = () => {
    const ids = confirm.parts.map((part) => part.id);
    closeConfirm();
    bulkSetStatus.mutate(
      { partIds: ids, partStatus: PartStatus.QC },
      {
        onSuccess: (response) => {
          const failed = response.data?.failed ?? [];
          const updated = response.data?.updated?.length ?? ids.length - failed.length;
          recordRun(
            "Send to QC",
            `${updated} sent to QC${failed.length > 0 ? `, ${failed.length} failed` : ""}`,
            failed.map((failure) => ({ partId: failure.partId, label: labelFor(rowsById.get(failure.partId), failure.partId), tone: "failed", text: failure.reason }))
          );
          addNotification({ primaryText: `${updated} part${updated === 1 ? "" : "s"} sent to QC`, variant: failed.length > 0 ? "warn" : "success" });
          if (focusedId && ids.includes(focusedId)) setFocusedId(null);
        },
        onError: () => addNotification({ primaryText: "Failed to send to QC", variant: "error" }),
      }
    );
  };

  const handleScrapOut = () => {
    const ids = confirm.parts.map((part) => part.id);
    closeConfirm();
    bulkScrapOut.mutate(ids, {
      onSuccess: (response) => {
        const failed = response.data?.failed ?? [];
        const updated = response.data?.updated?.length ?? ids.length - failed.length;
        recordRun(
          "Scrap out",
          `${updated} queued for scrap out${failed.length > 0 ? `, ${failed.length} failed` : ""}`,
          failed.map((failure) => ({ partId: failure.partId, label: labelFor(rowsById.get(failure.partId), failure.partId), tone: "failed", text: failure.reason }))
        );
        addNotification({ primaryText: `${updated} part${updated === 1 ? "" : "s"} queued for scrap out`, variant: failed.length > 0 ? "warn" : "success" });
        if (focusedId && ids.includes(focusedId)) setFocusedId(null);
      },
      onError: () => addNotification({ primaryText: "Failed to queue for scrap out", variant: "error" }),
    });
  };

  const handleDiscardSelected = () => {
    const ids = confirm.parts.map((part) => part.id).filter((id) => optimisationsByPartId.has(id));
    closeConfirm();
    discardChanges.mutate(ids, {
      onSuccess: () => addNotification({ primaryText: "Staged changes discarded", variant: "success" }),
      onError: () => addNotification({ primaryText: "Failed to discard staged changes", variant: "error" }),
    });
  };

  const handleStageFlag = (parts: IPart[], flag: "applyImageRules" | "removeBackgroundFirstImage" | "applyCategoryDefaults", value = true) => {
    if (parts.length === 0) return;
    stage(parts.map((part) => ({ ...buildCarryOverItem(part.id), [flag]: value })));
  };

  const handleStagePolicies = (policies: IBulkUpdatePolicies) => {
    const items = selectedRows.map((row) => {
      const carry = buildCarryOverItem(row.id);
      return {
        ...carry,
        newFulfillmentPolicyId: policies.fulfillmentPolicyId ?? carry.newFulfillmentPolicyId ?? null,
        newPaymentPolicyId: policies.paymentPolicyId ?? carry.newPaymentPolicyId ?? null,
        newReturnPolicyId: policies.returnPolicyId ?? carry.newReturnPolicyId ?? null,
        newLocationKey: policies.locationKey ?? carry.newLocationKey ?? null,
      };
    });
    stage(items, () => setPoliciesOpen(false));
  };

  // The lookup alone over the selection (the AI still filters the matches), written straight into the rows; nothing is staged.
  const handleLookupOnly = async () => {
    const rows = requireSelection();
    if (!rows) return;
    setLookupRunning(true);
    try {
      const response = await intelligencePricingBulk.mutateAsync(rows.map((row) => row.id));
      const results = response?.data ?? [];
      let found = 0;
      results.forEach((item) => {
        const summary = summarisePrices(item.results ?? []);
        if ((item.results ?? []).length > 0) found += 1;
        queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
          queryClient.setQueryData(key, (current: any) => {
            if (!current?.data?.rows) return current;
            return {
              ...current,
              data: {
                ...current.data,
                rows: current.data.rows.map((row: any) =>
                  row.id === item.partId
                    ? {
                        ...row,
                        intelligenceMin: summary?.min ?? null,
                        intelligenceAverage: summary?.avg ?? null,
                        intelligenceMax: summary?.max ?? null,
                        intelligenceActiveCount: (item.results ?? []).length,
                        lastIntelligenceLookup: new Date().toISOString(),
                      }
                    : row
                ),
              },
            };
          });
        });
      });
      recordRun("Intelligence lookup", `${found} of ${results.length} returned prices`, []);
      addNotification({ primaryText: `Intelligence updated for ${found} part${found === 1 ? "" : "s"}`, variant: found > 0 ? "success" : "warn" });
    } catch {
      addNotification({ primaryText: "Intelligence lookup failed", variant: "error" });
    } finally {
      setLookupRunning(false);
    }
  };

  const handleRegenerateDescriptions = () => {
    const rows = requireSelection();
    if (!rows) return;
    bulkUpdateProductDescription.mutate(
      { partIds: rows.map((row) => row.id) },
      {
        onSuccess: () => addNotification({ primaryText: `Descriptions regenerated for ${rows.length} part${rows.length === 1 ? "" : "s"}`, variant: "success" }),
        onError: () => addNotification({ primaryText: "Failed to regenerate descriptions", variant: "error" }),
      }
    );
  };

  const handlePhotoFlag = (part: IPart, flag: StagedImageFlag, value: boolean) => handleStageFlag([part], flag, value);

  const columns = useMemo(
    () =>
      buildQueueColumns({
        selectedIds,
        pageRows,
        onToggleRow: toggleRow,
        onToggleAll: toggleAll,
        optimisationsByPartId,
        findUserName,
        onOpenPhotos: setPhotosPart,
        onMarkReviewed: (part) => handleMarkReviewed([part]),
        markingReviewedIds: markingIds,
      }),
    [selectedIds, pageRows, toggleRow, toggleAll, optimisationsByPartId, findUserName, markingIds] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const summary =
    totalRows != null
      ? `Showing ${pageRows.length} of ${totalRows.toLocaleString("en-GB")}${filters.hideReviewedWithinDays != null ? ` · listings reviewed in the last ${filters.hideReviewedWithinDays} days are hidden` : ""}${selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ""}`
      : null;

  const confirmCount = confirm.parts.length;
  const stagedInConfirm = confirm.parts.filter((part) => optimisationsByPartId.has(part.id)).length;

  return (
    <>
      <RelistConfirmModal
        open={confirm.kind === "relist"}
        setOpen={(value) => !value && closeConfirm()}
        count={confirmCount}
        stagedCount={stagedInConfirm}
        onConfirm={handleRelist}
        isLoading={sendToRelist.isLoading}
      />
      <ConfirmModal
        open={confirm.kind === "qc"}
        setOpen={(value: boolean) => !value && closeConfirm()}
        title={confirmCount === 1 ? "Send this part to QC?" : `Send ${confirmCount} parts to QC?`}
        message={"The part moves to QC straight away for someone to check it. The listing stays live until it is withdrawn or sold."}
        confirmButtonText="Send to QC"
        onConfirm={handleSendToQc}
        isLoading={bulkSetStatus.isLoading}
      />
      <ConfirmModal
        open={confirm.kind === "scrapOut"}
        setOpen={(value: boolean) => !value && closeConfirm()}
        title={confirmCount === 1 ? "Scrap out this part?" : `Scrap out ${confirmCount} parts?`}
        message={"The tag is queued in the yard app's Scrap Out list. Nothing is scrapped or withdrawn until an operator scans it there - the listing ends then."}
        confirmButtonText="Scrap out"
        onConfirm={handleScrapOut}
        isLoading={bulkScrapOut.isLoading}
      />
      <ConfirmModal
        open={confirm.kind === "discard"}
        setOpen={(value: boolean) => !value && closeConfirm()}
        title="Discard staged changes?"
        message={`This removes the staged changes from ${stagedInConfirm} listing${stagedInConfirm === 1 ? "" : "s"}.\nThe live eBay listings are unaffected.`}
        confirmButtonText="Discard"
        onConfirm={handleDiscardSelected}
        isLoading={discardChanges.isLoading}
      />
      <BulkRelistResultModal open={relistResultOpen} setOpen={setRelistResultOpen} result={relistResult} />

      <BulkUpdatePoliciesModal
        open={policiesOpen}
        onClose={() => setPoliciesOpen(false)}
        onSubmit={handleStagePolicies}
        isLoading={stageChanges.isLoading}
        title="Stage policy changes"
        description="The policies you pick are staged against the selected listings and applied when they are relisted - nothing is sent to eBay yet. Leave a field empty to keep what is live."
        submitLabel="Stage policies"
        showLocation={true}
      />

      <Modal open={specificsOpen} setOpen={setSpecificsOpen} width="max-w-7xl">
        {specificsOpen && <BulkEditListingSpecifics parts={selectedRows} onClose={() => setSpecificsOpen(false)} updateOffer={false} />}
      </Modal>

      <PhotoManagerModal
        part={photosPart}
        open={photosPart != null}
        onClose={() => setPhotosPart(null)}
        optimisation={photosPart ? optimisationsByPartId.get(photosPart.id) : undefined}
        onStageFlag={handlePhotoFlag}
        staging={stageChanges.isLoading}
      />

      <FocusDrawer
        part={focusedPart}
        open={focusedPart != null}
        onClose={() => setFocusedId(null)}
        onPrev={() => focusOffset(-1)}
        onNext={() => focusOffset(1)}
        position={walkIndex >= 0 ? { index: walkIndex, total: walkList.length, source: walkList === selectedRows ? "selected" : "page" } : null}
        optimisation={focusedPart ? optimisationsByPartId.get(focusedPart.id) : undefined}
        buildCarryOverItem={buildCarryOverItem}
        onStage={(item, onSuccess) => stage([item], onSuccess)}
        staging={stageChanges.isLoading}
        onDiscard={(partId) => openConfirm("discard", [rowsById.get(partId)!].filter(Boolean))}
        discarding={discardChanges.isLoading}
        onRelist={(part) => openConfirm("relist", [part])}
        onMarkReviewed={(part, thenNext) => handleMarkReviewed([part], thenNext)}
        markingReviewed={markReviewed.isLoading}
        onOpenPhotos={setPhotosPart}
        onSendToQc={(part) => openConfirm("qc", [part])}
        onScrapOut={(part) => openConfirm("scrapOut", [part])}
      />

      <QueueFilters filters={filters} onChange={onFiltersChange} onClear={onClearFilters} summary={summary} />

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <BulkBar
          selectedCount={selectedIds.size}
          onClearSelection={() => setSelectedIds(new Set())}
          busy={{
            pricing: priceFromIntelligence.isLoading,
            aiTitles: generateAiTitles.isLoading,
            wilmaTitles: stageChanges.isLoading,
            reviewed: markReviewed.isLoading,
            relist: sendToRelist.isLoading,
            qc: bulkSetStatus.isLoading,
            scrapOut: bulkScrapOut.isLoading,
          }}
          onIntelligentPricing={handleIntelligentPricing}
          onAiTitles={handleAiTitles}
          onStageWilmaTitles={handleStageWilmaTitles}
          onMarkReviewed={() => handleMarkReviewed(selectedRows)}
          onRelist={() => openConfirm("relist", selectedRows)}
          onSendToQc={() => openConfirm("qc", selectedRows)}
          onScrapOut={() => openConfirm("scrapOut", selectedRows)}
          moreActions={[
            { key: "lookup", label: lookupRunning ? "Looking up intelligence…" : "Intelligence lookup only (no price staged)", onClick: handleLookupOnly, disabled: lookupRunning },
            { key: "policies", label: "Stage policies and location", onClick: () => (requireSelection() ? setPoliciesOpen(true) : undefined) },
            { key: "specifics", label: "Edit item specifics (saves to the parts)", onClick: () => (requireSelection() ? setSpecificsOpen(true) : undefined) },
            { key: "imageRules", label: "Apply image rules on relist", onClick: () => handleStageFlag(selectedRows, "applyImageRules") },
            { key: "removeBg", label: "Remove background on 1st image on relist", onClick: () => handleStageFlag(selectedRows, "removeBackgroundFirstImage") },
            { key: "defaults", label: "Apply category defaults on relist", onClick: () => handleStageFlag(selectedRows, "applyCategoryDefaults") },
            { key: "descriptions", label: "Regenerate descriptions from template", onClick: handleRegenerateDescriptions, disabled: bulkUpdateProductDescription.isLoading },
            {
              key: "discard",
              label: "Discard staged changes on selection",
              onClick: () => openConfirm("discard", selectedRows.filter((row) => optimisationsByPartId.has(row.id))),
              disabled: !selectedRows.some((row) => optimisationsByPartId.has(row.id)),
              tone: "danger",
            },
          ]}
          lastRun={lastRun}
        />

        <PagedTable<IPart>
          url="/part/listings"
          pageMode={PageMode.Listed}
          columns={columns}
          externalQuery={filters.search}
          showTotal={true}
          additionalBody={additionalBody}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          setRowData={handleRowData}
          onTotalRowsChange={setTotalRows}
          onRowClick={(row) => setFocusedId(row.id)}
        />
      </div>
    </>
  );
}

export { defaultQueueFilters };
